import { openDB } from "idb";
import { v4 as uuid } from "uuid";

/*
 * Offline support module.
 *
 *   - `parts_cache` object store: a single key "all" holding the latest parts array.
 *   - `queue` object store: pending mutations, drained on reconnect.
 */

const DB_NAME = "partsgrid-offline";
const DB_VERSION = 1;

async function db() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(d) {
      if (!d.objectStoreNames.contains("queue")) {
        d.createObjectStore("queue", { keyPath: "id" });
      }
      if (!d.objectStoreNames.contains("parts_cache")) {
        d.createObjectStore("parts_cache");
      }
    },
  });
}

// ---------- Parts catalog cache ----------
export async function cacheParts(parts) {
  const d = await db();
  await d.put("parts_cache", { parts, at: new Date().toISOString() }, "all");
}
export async function getCachedParts() {
  const d = await db();
  const rec = await d.get("parts_cache", "all");
  return rec?.parts || [];
}
export async function getCachedPartByNumber(part_number) {
  const parts = await getCachedParts();
  return parts.find((p) => p.part_number === part_number) || null;
}

// ---------- Mutation queue ----------
/**
 * Queue a mutation to be replayed on reconnect.
 * `op` shape: { method, url, body, label, idempotency_key? }
 *
 * We snapshot the current auth token + active branch onto every queued item so
 * the service worker's Background Sync handler can replay it even when no tab
 * is open (no access to localStorage from a SW). If the token expires by the
 * time we drain, the request will 401 → failed_permanent and the user can
 * manually retry after re-authenticating.
 */
export async function enqueue(op) {
  const id = uuid();
  const item = {
    id,
    idempotency_key: op.idempotency_key || uuid(),
    method: op.method,
    url: op.url,
    body: op.body,
    label: op.label || `${op.method} ${op.url}`,
    auth_token: localStorage.getItem("auth_token") || null,
    branch_id: localStorage.getItem("active_branch_id") || null,
    queued_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
    status: "pending",
  };
  // Embed idempotency_key into body so the backend can de-dupe
  if (item.body && typeof item.body === "object") {
    item.body = { ...item.body, idempotency_key: item.idempotency_key };
  }
  const d = await db();
  await d.put("queue", item);
  notifyListeners();
  // Hand off to the SW's Background Sync so replay can happen even if the tab closes.
  registerBackgroundSync();
  return item;
}

export async function getQueue() {
  const d = await db();
  return d.getAll("queue");
}

export async function removeFromQueue(id) {
  const d = await db();
  await d.delete("queue", id);
  notifyListeners();
}

export async function updateQueueItem(id, patch) {
  const d = await db();
  const item = await d.get("queue", id);
  if (item) {
    await d.put("queue", { ...item, ...patch });
    notifyListeners();
  }
}

// ---------- Listeners (for UI status badge) ----------
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyListeners() {
  getQueue().then((q) => listeners.forEach((fn) => fn({ queue: q })));
}

// ---------- Replay queue ----------
import { api } from "@/lib/api";

let _draining = false;
export async function drainQueue() {
  if (_draining) return { skipped: true };
  if (!navigator.onLine) return { skipped: true, reason: "offline" };
  _draining = true;
  const results = { succeeded: 0, failed: 0 };
  try {
    const q = await getQueue();
    // Process in queued order
    q.sort((a, b) => a.queued_at.localeCompare(b.queued_at));
    for (const item of q) {
      if (item.status === "failed_permanent") continue;
      try {
        await api.request({
          method: item.method,
          url: item.url,
          data: item.body,
        });
        await removeFromQueue(item.id);
        results.succeeded += 1;
      } catch (err) {
        const httpStatus = err?.response?.status;
        const detail = err?.response?.data?.detail || err.message;
        // 4xx (other than 408/429) is a permanent failure — won't fix by retrying
        const permanent = httpStatus && httpStatus >= 400 && httpStatus < 500 && ![408, 425, 429].includes(httpStatus);
        await updateQueueItem(item.id, {
          attempts: item.attempts + 1,
          last_error: typeof detail === "string" ? detail : JSON.stringify(detail),
          status: permanent ? "failed_permanent" : "pending",
        });
        results.failed += 1;
        if (!permanent) break; // network-ish error — stop and try later
      }
    }
  } finally {
    _draining = false;
  }
  return results;
}

// ---------- Pending stock deltas (offline preview) ----------
/**
 * Walk the queue and compute how many units of each part are already committed
 * to pending mutations (offline sales, direct invoices, orders). Returned as a
 * Map<part_id, qty> that the Parts UI subtracts from the on-server stock to
 * show `-N pending` badges.
 *
 * We only count `pending`/`retrying` items — `failed_permanent` items have
 * been abandoned by the backend and no longer represent live commitments.
 */
export async function getPendingStockDeltas() {
  const d = await db();
  const items = await d.getAll("queue");
  const deltas = new Map();
  for (const it of items) {
    if (it.status !== "pending" && it.status !== "retrying") continue;
    const body = it.body;
    if (!body || !Array.isArray(body.items)) continue;
    // Only decrement for mutations that actually consume stock
    const isSale = /\/(invoices\/direct|orders)$/.test(it.url || "");
    if (!isSale) continue;
    for (const line of body.items) {
      if (!line?.part_id) continue;
      const qty = Number(line.qty) || 0;
      if (qty <= 0) continue;
      deltas.set(line.part_id, (deltas.get(line.part_id) || 0) + qty);
    }
  }
  return deltas;
}
/**
 * Purge auth tokens embedded in queued mutations. Called on logout so a
 * subsequent user of the same device can't accidentally replay work under
 * the previous account, and so an XSS payload can't harvest a still-valid
 * JWT from IndexedDB after the tab has "logged out".
 *
 * The queued mutations themselves are retained (still visible in the Sync
 * Queue page as failed_permanent) so operators know work needs to be redone
 * — they simply cannot be replayed automatically without a fresh token.
 */
export async function purgeQueuedAuthTokens() {
  const d = await db();
  const items = await d.getAll("queue");
  const tx = d.transaction("queue", "readwrite");
  for (const it of items) {
    if (it.auth_token) {
      tx.store.put({
        ...it,
        auth_token: null,
        status: "failed_permanent",
        last_error: "Signed out — resubmit after signing back in",
      });
    }
  }
  await tx.done;
  notifyListeners();
}

// ---------- Auto-sync on reconnect ----------
let _initialised = false;
export function initOfflineSync() {
  if (_initialised) return;
  _initialised = true;
  window.addEventListener("online", () => { drainQueue(); registerBackgroundSync(); });
  // also try right after init in case we boot online with a backlog
  setTimeout(() => { drainQueue(); }, 1500);
  // poll every 60s as a safety net (some browsers fire 'online' inconsistently)
  setInterval(() => { if (navigator.onLine) drainQueue(); }, 60_000);

  // Refresh UI whenever the SW background-sync completes a drain
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "queue-drained") {
        notifyListeners();
      }
    });
  }
}

// ---------- Background Sync registration ----------
/**
 * Register a background-sync tag with the active service worker. When the OS
 * next detects connectivity (even if all our tabs are closed), the SW's `sync`
 * handler will drain the IndexedDB queue.
 *
 * Silently no-ops on browsers without Background Sync (Safari, Firefox) —
 * those fall back to the foreground drain-on-reconnect path.
 */
export async function registerBackgroundSync() {
  if (typeof navigator === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (!("SyncManager" in window)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (reg.sync) await reg.sync.register("partsgrid-drain-queue");
  } catch {
    // permission denied / not supported — foreground path still works
  }
}
